import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { ChatState, Message } from '@/types/chat';
import { threadService } from '@/services/threads';
import { createAsyncThunk } from '@reduxjs/toolkit';

const initialState: ChatState = {
  chats: [],
  isLoading: false,
  error: null,
  loadingByPersona: {},
};

// Async thunk to initialize a chat with thread management
export const initializeChatWithThread = createAsyncThunk(
  'chat/initializeChatWithThread',
  async ({ 
    personaId,
    personaName, 
    userPrompt,
    resetThread = false
  }: { 
    personaId: string;
    personaName: string; 
    userPrompt?: string;
    resetThread?: boolean;
  }, { getState }) => {
    const state = getState() as any;
    const threads = state.thread.threads;

    // Helper function to convert interactions to messages
    const convertInteractionsToMessages = (interactions: any[]) => {
      return interactions.flatMap((interaction, index) => [
        // User message
        {
          id: `${interaction.interactionID}-user-${index}`,
          text: interaction.userPrompt,
          sender: 'user' as const,
          timestamp: new Date().toISOString(),
        },
        // Bot message
        {
          id: `${interaction.interactionID}-bot-${index}`,
          text: interaction.assistResponse,
          sender: 'bot' as const,
          timestamp: new Date().toISOString(),
        }
      ]);
    };

    let threadId: string | null;
    let messages: Message[] = [];
    let isExistingThread = false;

    const existingThread = threads.find((thread: any) => thread.personaId === personaId);

    if (resetThread || !existingThread) {
      // If it's a reset or no thread exists, create a new thread but DON'T send messages
      if (userPrompt) {
        // Only if there's a userPrompt, create the thread and send the message
        const response = await threadService.createThread({
          personaId: personaId,
          userPrompt: userPrompt,
        });
        console.log('response', response);
        threadId = response.threadId;
        
        // Load interactions from the newly created thread
        const threadInfo = await threadService.getThreadInfo(threadId);
        messages = convertInteractionsToMessages(threadInfo.interactions);
      } else {
        // If there's no userPrompt, don't create the thread yet
        // The thread will be created when the user sends the first message
        return {
          personaName,
          threadId: null,
          messages: [],
          isExistingThread: false,
          isReset: resetThread,
          needsFirstMessage: true,
        };
      }
    } else {
      // Thread exists, load messages
      threadId = existingThread.threadId;
      isExistingThread = true;
      
      if (threadId) {
        const threadInfo = await threadService.getThreadInfo(threadId);
        messages = convertInteractionsToMessages(threadInfo.interactions);
      }
    }

    return {
      personaName,
      threadId,
      messages,
      isExistingThread,
      isReset: resetThread,
      needsFirstMessage: false,
    };
  }
);

// Async thunk to send the first message and create the thread
export const sendFirstMessage = createAsyncThunk(
  'chat/sendFirstMessage',
  async ({ 
    personaId,
    personaName, 
    userPrompt
  }: { 
    personaId: string;
    personaName: string; 
    userPrompt: string;
  }, { dispatch }) => {
    // Create the thread with the first message
    const response = await threadService.createThread({
      personaId: personaId,
      userPrompt: userPrompt,
    });
    
    console.log('First message response', response);
    
    // Update the threadId in the chat
    dispatch(updateThreadId({ personaName, threadId: response.threadId }));
    
    // Add messages to the chat
    const userMessage: Message = {
      id: `user-${Date.now()}`,
      text: userPrompt,
      sender: 'user',
      timestamp: new Date().toISOString(),
    };
    
    const botMessage: Message = {
      id: `bot-${Date.now()}`,
      text: response.assistResponse || 'Response received',
      sender: 'bot',
      timestamp: new Date().toISOString(),
    };
    
    dispatch(addMessage({ personaName, message: userMessage }));
    dispatch(addMessage({ personaName, message: botMessage }));
    
    return {
      threadId: response.threadId,
      assistant: response.assistResponse,
    };
  }
);

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    // Add a message to a specific persona's chat
    addMessage: (state, action: PayloadAction<{ personaName: string; message: Message }>) => {
      const { personaName, message } = action.payload;
      const existingChat = state.chats.find(chat => chat.personaName === personaName);
      
      if (existingChat) {
        existingChat.messages.push(message);
      } else {
        state.chats.push({
          personaName,
          messages: [message],
          unreadCount: message.sender === 'bot' ? 1 : 0,
        });
      }
    },

    // Clear a specific persona's chat
    clearChat: (state, action: PayloadAction<string>) => {
      const personaName = action.payload;
      const chatIndex = state.chats.findIndex(chat => chat.personaName === personaName);
      
      if (chatIndex !== -1) {
        state.chats[chatIndex].messages = [];
        state.chats[chatIndex].unreadCount = 0;
      }
    },

    // Clear all chats
    clearAllChats: (state) => {
      state.chats = [];
    },

    // Set the loading state
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    // Set the error
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    // Reset the unread message counter for a persona
    markChatAsRead: (state, action: PayloadAction<string>) => {
      const personaName = action.payload;
      const chat = state.chats.find(chat => chat.personaName === personaName);
      if (chat) {
        chat.unreadCount = 0;
      }
    },

    // Increment the unread message counter for a persona
    incrementUnreadCount: (state, action: PayloadAction<string>) => {
      const personaName = action.payload;
      const chat = state.chats.find(chat => chat.personaName === personaName);
      if (chat) {
        chat.unreadCount += 1;
      }
    },

    setPersonaLoading: (state, action: PayloadAction<{ personaName: string; loading: boolean }>) => {
      state.loadingByPersona[action.payload.personaName] = action.payload.loading;
    },

    // Update the threadId of a specific chat
    updateThreadId: (state, action: PayloadAction<{ personaName: string; threadId: string }>) => {
      const { personaName, threadId } = action.payload;
      const chat = state.chats.find(chat => chat.personaName === personaName);
      if (chat) {
        chat.threadId = threadId;
      }
    },

    // Reset the chat of a persona (clear messages and remove the threadId)
    resetChat: (state, action: PayloadAction<string>) => {
      const personaName = action.payload;
      const chat = state.chats.find(chat => chat.personaName === personaName);
      if (chat) {
        chat.messages = [];
        chat.unreadCount = 0;
        chat.threadId = undefined;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(initializeChatWithThread.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    
    builder.addCase(initializeChatWithThread.fulfilled, (state, action) => {
      const { personaName, threadId, messages, isExistingThread, isReset } = action.payload;
      const existingChat = state.chats.find(chat => chat.personaName === personaName);
      
      if (existingChat) {
        // Update the existing chat with the threadId and messages
        existingChat.threadId = threadId || undefined;
        if (isReset) {
          // If it is a reset, replace the messages completely
          existingChat.messages = messages as Message[];
          existingChat.unreadCount = 0;
        } else if (isExistingThread) {
          // If it is an existing thread, replace the messages with the thread's messages
          existingChat.messages = messages as Message[];
        }
        // If needsFirstMessage is true, do nothing (the thread will be created on first message)
      } else {
        // Create a new chat
        state.chats.push({
          personaName,
          messages: messages as Message[],
          unreadCount: 0,
          threadId: threadId || undefined,
        });
      }
      state.isLoading = false;
    });
    
    builder.addCase(initializeChatWithThread.rejected, (state, action) => {
      state.error = action.error.message || 'Error during chat initialization';
      state.isLoading = false;
    });
    
    // Handle sendFirstMessage
    builder.addCase(sendFirstMessage.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    
    builder.addCase(sendFirstMessage.fulfilled, (state) => {
      state.isLoading = false;
    });
    
    builder.addCase(sendFirstMessage.rejected, (state, action) => {
      state.error = action.error.message || 'Error sending first message';
      state.isLoading = false;
    });
  },
});

export const {
  addMessage,
  clearChat,
  clearAllChats,
  setLoading,
  setError,
  markChatAsRead,
  incrementUnreadCount,
  setPersonaLoading,
  updateThreadId,
  resetChat,
} = chatSlice.actions;


export default chatSlice.reducer; 